import { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { Download, UserX, MailCheck, Globe, ChevronDown, ChevronUp } from "lucide-react";

const REGION_LABELS = {
  all:         "All",
  europe:      "Europe",
  asia:        "Asia",
  americas:    "Americas",
  africa:      "Africa",
  middle_east: "Middle East",
  oceania:     "Oceania",
};

const REGION_KEYS = ["europe", "asia", "americas", "africa", "middle_east", "oceania"];

const REGION_FILTER_OPTIONS = [
  { value: "",            label: "All regions" },
  { value: "all",         label: "All regions (pref)" },
  { value: "europe",      label: "Europe" },
  { value: "asia",        label: "Asia" },
  { value: "americas",    label: "Americas" },
  { value: "africa",      label: "Africa" },
  { value: "middle_east", label: "Middle East" },
  { value: "oceania",     label: "Oceania" },
];

const NOW_MONTH = new Date().toLocaleString("en-US", { month: "long", year: "numeric" });

const EMAIL_TYPES = [
  { value: "preferences",   label: "PREFERENCES UPDATE" },
  { value: "deal_alert",    label: "DEAL ALERT" },
  { value: "newsletter",    label: "NEWSLETTER" },
  { value: "announcement",  label: "ANNOUNCEMENT" },
];

const TYPE_TEMPLATES = {
  preferences: {
    subject:     "Your Restricted Deals alerts just got smarter",
    heading:     "// CHOOSE YOUR DEAL REGIONS",
    body_text:   "You're currently receiving all deal alerts — flights, hotels and cruises from every region.\n\nWe've just added a new feature: you can now choose which regions you want deals for.\n\nOnly interested in European flights? Or maybe Middle East and Asia? Update your preference in 10 seconds and we'll only send what's relevant to you.\n\nNo change needed if you're happy receiving everything — you'll keep getting all deals as normal.",
    button_text: "CHOOSE MY REGIONS →",
    custom_link: "",
    footer_note: "You're receiving this because you subscribed to Restricted Deals. You can update your preferences anytime or unsubscribe with one click.",
  },
  deal_alert: {
    subject:     "// RESTRICTED DEAL — [route] from [price]",
    heading:     "// RESTRICTED DEAL",
    body_text:   "A new deal has just dropped. Edit the subject and body above with the route, airline, and price details.",
    button_text: "VIEW DEAL →",
    custom_link: "",
    footer_note: "You're receiving this because you subscribed to Restricted Deals deal alerts. Unsubscribe anytime.",
  },
  newsletter: {
    subject:     `// RESTRICTED DEALS — ${NOW_MONTH} roundup`,
    heading:     "// MONTHLY ROUNDUP",
    body_text:   "Here's what dropped this month across all our deal categories. Flights, hotels, cruises, and packages — the best prices we spotted.",
    button_text: "VIEW ALL DEALS →",
    custom_link: "https://restricteddeals.com",
    footer_note: "You're receiving this because you subscribed to Restricted Deals. Unsubscribe anytime.",
  },
  announcement: {
    subject:     "// ANNOUNCEMENT from Restricted Deals",
    heading:     "// ANNOUNCEMENT",
    body_text:   "",
    button_text: "",
    custom_link: "",
    footer_note: "You're receiving this because you subscribed to Restricted Deals. Unsubscribe anytime.",
  },
};

function RegionBadges({ regions }) {
  const prefs = regions?.length ? regions : ["all"];
  return (
    <div className="flex flex-wrap gap-1">
      {prefs.map((r) => (
        <span
          key={r}
          className={`inline-block font-mono text-[0.6rem] tracking-widest uppercase px-1.5 py-0.5 border ${
            r === "all"
              ? "border-[#333] text-[#555]"
              : "border-[#CC1A1A]/40 text-[#CC1A1A]"
          }`}
        >
          {REGION_LABELS[r] || r}
        </span>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, multiline, rows = 4, placeholder }) {
  const cls = "w-full bg-[#0E0E0E] border border-[#2A2A2A] text-[#E5E0D8] font-mono text-xs px-3 py-2.5 outline-none focus:border-[#CC1A1A] transition-colors resize-none";
  return (
    <div>
      <label className="block text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-1">{label}</label>
      {multiline ? (
        <textarea rows={rows} className={cls} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <input type="text" className={cls} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}

const TYPE_LABELS = {
  preferences:  "PREFS UPDATE",
  deal_alert:   "DEAL ALERT",
  newsletter:   "NEWSLETTER",
  announcement: "ANNOUNCEMENT",
};

export default function AdminSubscribersPage() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);
  const [regionFilter, setRegionFilter] = useState("");

  // Broadcast composer state
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [emailType, setEmailType] = useState("preferences");
  const [content, setContent] = useState({ ...TYPE_TEMPLATES.preferences });
  const [recipientRegion, setRecipientRegion] = useState("");
  const [testTo, setTestTo] = useState("hello@restricteddeals.com");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingLive, setSendingLive] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const setField = (key) => (val) => setContent((p) => ({ ...p, [key]: val }));

  const handleTypeChange = (type) => {
    setEmailType(type);
    setContent({ ...TYPE_TEMPLATES[type] });
    setTestSent(false);
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).finally(() => setLoading(false));
  };

  const loadHistory = () => {
    setHistoryLoading(true);
    api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch(() => {}).finally(() => setHistoryLoading(false));
  };

  useEffect(load, []);

  useEffect(() => {
    if (showBroadcast) loadHistory();
  }, [showBroadcast]);

  const revoke = async (s) => {
    if (!window.confirm(`Unsubscribe ${s.email}? They'll stop receiving emails immediately.`)) return;
    try {
      await api.post(`/admin/subscribers/${s.id}/unsubscribe`);
      toast.success(`${s.email.toUpperCase()} — ACCESS REVOKED`);
      load();
    } catch {
      toast.error("Failed to unsubscribe");
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/subscribers/send-broadcast-test", {
        ...content,
        email_type: emailType,
        region_filter: recipientRegion,
        test_to: testTo,
      });
      setTestSent(true);
      setTestEmail(data.to || testTo);
      toast.success(`TEST SENT → ${data.to} — check your inbox before sending to all.`);
    } catch {
      toast.error("Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const sendLive = async () => {
    if (recipientCount === 0) {
      toast.error("NO ELIGIBLE SUBSCRIBERS match this filter.");
      return;
    }
    if (!window.confirm(`Send broadcast to ${recipientCount} confirmed subscriber${recipientCount === 1 ? "" : "s"}?`)) return;
    setSendingLive(true);
    try {
      const { data } = await api.post("/admin/subscribers/send-broadcast", {
        ...content,
        email_type: emailType,
        region_filter: recipientRegion,
      });
      toast.success(
        data.count === 0
          ? "NO ELIGIBLE SUBSCRIBERS — nothing sent."
          : `QUEUED — broadcast sending to ${data.count} operator${data.count === 1 ? "" : "s"}.`
      );
      setTestSent(false);
      loadHistory();
    } catch {
      toast.error("Failed to send broadcast");
    } finally {
      setSendingLive(false);
    }
  };

  const sendReminders = async () => {
    if (!window.confirm("Send confirmation reminders to all pending subscribers who signed up 1h–7 days ago and haven't been reminded yet?")) return;
    setReminding(true);
    try {
      const { data } = await api.post("/admin/subscribers/send-confirmation-reminder");
      if (data.reminders_sent === 0) {
        toast.success("NO ELIGIBLE SUBSCRIBERS — all pending operators already reminded or too recent.");
      } else {
        toast.success(`REMINDERS SENT — ${data.reminders_sent} operator${data.reminders_sent === 1 ? "" : "s"} notified.`);
      }
    } catch {
      toast.error("Failed to send reminders");
    } finally {
      setReminding(false);
    }
  };

  const byStatus = (s) => subs.filter((x) => x.status === s).length;
  const confirmed = subs.filter((s) => s.status === "confirmed");

  const recipientCount = recipientRegion
    ? confirmed.filter((s) => (s.regions || ["all"]).includes(recipientRegion)).length
    : confirmed.length;

  const eligibleForPrefs = confirmed.filter((s) => !s.preferences_email_sent).length;

  const filtered = regionFilter
    ? subs.filter((s) => (s.regions || ["all"]).includes(regionFilter))
    : subs;

  const inputCls = "w-full bg-[#0E0E0E] border border-[#2A2A2A] text-[#E5E0D8] font-mono text-xs px-3 py-2.5 outline-none focus:border-[#CC1A1A] transition-colors";

  return (
    <div className="max-w-6xl" data-testid="admin-subscribers-page">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="eyebrow eyebrow-red">// ROSTER</p>
          <h1 className="font-mono text-4xl md:text-5xl text-white mt-2 uppercase tracking-tight">Operators</h1>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          <button
            onClick={() => setShowBroadcast((v) => !v)}
            className={`btn-rd-outline ${showBroadcast ? "!border-[#CC1A1A] !text-[#CC1A1A]" : ""}`}
            data-testid="broadcast-btn"
          >
            <Globe className="w-4 h-4" />
            BROADCAST EMAIL
            {showBroadcast ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <button
            onClick={sendReminders}
            disabled={reminding}
            className="btn-rd-outline disabled:opacity-60"
            data-testid="send-reminders-btn"
          >
            <MailCheck className="w-4 h-4" /> {reminding ? "SENDING…" : "SEND REMINDERS"}
          </button>
          <a href={`${API}/admin/subscribers/export.csv`} className="btn-rd" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> EXPORT CSV
          </a>
        </div>
      </header>

      {/* ── Broadcast Composer ── */}
      {showBroadcast && (
        <div className="mb-8 border border-[#2A2A2A] bg-[#0E0E0E]">
          {/* Header */}
          <div className="px-5 py-4 border-b border-[#2A2A2A]">
            <p className="eyebrow eyebrow-red text-[10px]">// BROADCAST EMAIL</p>
            <p className="font-mono text-xs text-[#9A8F83] mt-1 tracking-wide">
              Compose and send an email to your subscriber roster
            </p>
          </div>

          <div className="px-5 py-5 space-y-5">
            {/* EMAIL TYPE */}
            <div>
              <label className="block text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-2">Email type</label>
              <div className="flex flex-wrap gap-2">
                {EMAIL_TYPES.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleTypeChange(value)}
                    className={`font-mono text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${
                      emailType === value
                        ? "border-white text-white bg-[#1A1A1A]"
                        : "border-[#3A3A3A] text-[#9A8F83] hover:border-[#9A8F83] hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {emailType === "preferences" && (
                <p className="mt-2 text-[10px] font-mono text-[#555] tracking-wide">
                  {eligibleForPrefs} eligible subscriber{eligibleForPrefs !== 1 ? "s" : ""} haven't received this yet
                </p>
              )}
            </div>

            {/* RECIPIENT FILTER */}
            <div>
              <label className="block text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-2">Recipients</label>
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  onClick={() => setRecipientRegion("")}
                  className={`font-mono text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${
                    recipientRegion === ""
                      ? "border-white text-white bg-[#1A1A1A]"
                      : "border-[#3A3A3A] text-[#9A8F83] hover:border-[#9A8F83] hover:text-white"
                  }`}
                >
                  ALL REGIONS
                </button>
                {REGION_KEYS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRecipientRegion(r)}
                    className={`font-mono text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${
                      recipientRegion === r
                        ? "border-[#CC1A1A] text-[#CC1A1A] bg-[#1A1A1A]"
                        : "border-[#3A3A3A] text-[#9A8F83] hover:border-[#9A8F83] hover:text-white"
                    }`}
                  >
                    {REGION_LABELS[r]}
                  </button>
                ))}
              </div>
              <p className="text-[10px] font-mono tracking-wide" style={{ color: recipientCount > 0 ? "#9A8F83" : "#CC1A1A" }}>
                Sending to: <span className="text-white">{recipientCount}</span> confirmed subscriber{recipientCount !== 1 ? "s" : ""}
              </p>
            </div>

            {/* CONTENT */}
            <Field label="Subject line" value={content.subject} onChange={setField("subject")} />
            <Field label="Email heading" value={content.heading} onChange={setField("heading")} />
            <Field label="Body text" value={content.body_text} onChange={setField("body_text")} multiline rows={5} />
            {emailType !== "preferences" && (
              <>
                <Field label="Button text" value={content.button_text} onChange={setField("button_text")} placeholder="LEARN MORE →" />
                <Field label="Button URL (optional)" value={content.custom_link} onChange={setField("custom_link")} placeholder="https://restricteddeals.com/deal/..." />
              </>
            )}
            <Field label="Footer note" value={content.footer_note} onChange={setField("footer_note")} />
          </div>

          {/* Test sent confirmation */}
          {testSent && (
            <div className="mx-5 mb-4 px-4 py-3 border border-[#CC1A1A]/30 bg-[#CC1A1A]/5">
              <p className="font-mono text-xs text-[#CC1A1A] tracking-wide">
                ✓ Test sent to {testEmail} — check your inbox before sending to all subscribers
              </p>
            </div>
          )}

          {/* Test address */}
          <div className="px-5 pb-3">
            <label className="block text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-1">Send test to</label>
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className={inputCls}
              placeholder="hello@restricteddeals.com"
              data-testid="test-email-input"
            />
          </div>

          {/* Actions */}
          <div className="px-5 pb-5 flex flex-col sm:flex-row gap-3">
            <button
              onClick={sendTest}
              disabled={sendingTest || sendingLive || !testTo}
              className="btn-rd-outline disabled:opacity-60 flex-1 justify-center"
              data-testid="send-broadcast-test-btn"
            >
              {sendingTest ? "SENDING…" : "SEND TEST →"}
            </button>
            <button
              onClick={sendLive}
              disabled={!testSent || sendingLive || sendingTest || recipientCount === 0}
              className="btn-rd disabled:opacity-50 flex-1 justify-center"
              data-testid="send-broadcast-live-btn"
              title={!testSent ? "Send a test email first" : undefined}
            >
              {sendingLive ? "SENDING…" : `BROADCAST TO ${recipientCount} →`}
            </button>
          </div>
          {!testSent && (
            <p className="px-5 pb-4 text-[10px] font-mono text-[#444] tracking-widest uppercase">
              // send a test email first to enable live broadcast
            </p>
          )}

          {/* Send History */}
          <div className="border-t border-[#2A2A2A] px-5 py-4">
            <p className="eyebrow text-[10px] mb-3">// SEND HISTORY</p>
            {historyLoading ? (
              <p className="font-mono text-xs text-[#555] tracking-widest">// loading…</p>
            ) : history.length === 0 ? (
              <p className="font-mono text-xs text-[#555] tracking-widest uppercase">// No broadcasts sent yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[540px]">
                  <thead>
                    <tr className="text-left">
                      <th className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] pb-2 pr-4">DATE</th>
                      <th className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] pb-2 pr-4">TYPE</th>
                      <th className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] pb-2 pr-4">SUBJECT</th>
                      <th className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] pb-2 pr-4">REGION</th>
                      <th className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#555] pb-2 text-right">RECIPIENTS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1A1A1A]">
                    {history.map((h) => (
                      <tr key={h.id}>
                        <td className="py-2 pr-4 font-mono text-xs text-[#9A8F83] whitespace-nowrap">
                          {new Date(h.sent_at).toLocaleDateString()}
                        </td>
                        <td className="py-2 pr-4">
                          <span className="rd-badge rd-badge-dim !text-[0.55rem]">{TYPE_LABELS[h.email_type] || h.email_type}</span>
                        </td>
                        <td className="py-2 pr-4 font-mono text-xs text-[#E5E0D8] max-w-[200px] truncate">{h.subject}</td>
                        <td className="py-2 pr-4 font-mono text-xs text-[#9A8F83] uppercase">
                          {h.region_filter === "all" || !h.region_filter ? "ALL" : REGION_LABELS[h.region_filter] || h.region_filter}
                        </td>
                        <td className="py-2 font-mono text-xs text-white text-right">{h.recipient_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
        <Stat label="TOTAL" value={subs.length} />
        <Stat label="CLEARED" value={byStatus("confirmed")} accent />
        <Stat label="PENDING" value={byStatus("pending")} />
        <Stat label="REVOKED" value={byStatus("unsubscribed")} />
      </div>

      {/* Region filter */}
      <div className="mb-6 flex items-center gap-3">
        <span className="text-[10px] font-mono tracking-widest uppercase text-[#555]">// FILTER BY REGION</span>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          className="bg-[#0E0E0E] border border-[#2A2A2A] text-[#E5E0D8] font-mono text-xs px-3 py-2 outline-none focus:border-[#CC1A1A] transition-colors"
          data-testid="region-filter"
        >
          {REGION_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {regionFilter && (
          <span className="text-[10px] font-mono text-[#555]">
            {filtered.length} of {subs.length} operators
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-xs font-mono text-[#9A8F83] tracking-widest uppercase">// loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rd-card p-12 text-center" data-testid="subscribers-empty">
          <p className="font-mono text-2xl text-white uppercase tracking-tight">// NO OPERATORS</p>
        </div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {filtered.map((s) => (
              <div key={s.id} className="rd-card p-4" data-testid={`sub-row-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-white text-sm break-all">{s.email}</p>
                    {s.first_name && (
                      <p className="text-[#9A8F83] font-mono text-xs mt-0.5">{s.first_name}</p>
                    )}
                  </div>
                  <span
                    className={`rd-badge shrink-0 ${
                      s.status === "confirmed" ? "" : s.status === "unsubscribed" ? "rd-badge-outline" : "rd-badge-dim"
                    }`}
                  >
                    {s.status.toUpperCase()}
                  </span>
                </div>
                <div className="mt-2">
                  <RegionBadges regions={s.regions} />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-[#9A8F83] font-mono text-xs tracking-wider">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                  </p>
                  {s.status !== "unsubscribed" && (
                    <button
                      onClick={() => revoke(s)}
                      className="btn-rd-outline !py-1.5 !px-3 !text-[0.6rem] hover:!border-[#CC1A1A] hover:!text-[#CC1A1A]"
                      data-testid={`sub-revoke-${s.id}`}
                    >
                      <UserX className="w-3 h-3" /> REVOKE
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block rd-card overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="bg-[#0E0E0E] text-left">
                <tr>
                  <th className="eyebrow px-4 py-3">NAME</th>
                  <th className="eyebrow px-4 py-3">EMAIL</th>
                  <th className="eyebrow px-4 py-3">STATUS</th>
                  <th className="eyebrow px-4 py-3">REGIONS</th>
                  <th className="eyebrow px-4 py-3">ENROLLED</th>
                  <th className="eyebrow px-4 py-3 text-right">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2A2A2A]">
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`sub-row-${s.id}`}>
                    <td className="px-4 py-3 font-mono text-[#E5E0D8]">
                      {s.first_name || <span className="text-[#555]">—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-white break-all">{s.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rd-badge ${
                          s.status === "confirmed"
                            ? ""
                            : s.status === "unsubscribed"
                            ? "rd-badge-outline"
                            : "rd-badge-dim"
                        }`}
                      >
                        {s.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <RegionBadges regions={s.regions} />
                    </td>
                    <td className="px-4 py-3 text-[#9A8F83] font-mono text-xs tracking-wider">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.status !== "unsubscribed" && (
                        <button
                          onClick={() => revoke(s)}
                          className="btn-rd-outline !py-1.5 !px-3 !text-[0.6rem] hover:!border-[#CC1A1A] hover:!text-[#CC1A1A]"
                          data-testid={`sub-revoke-${s.id}`}
                          title="Unsubscribe this operator"
                        >
                          <UserX className="w-3 h-3" /> REVOKE
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rd-card p-5">
      <p className="eyebrow">{label}</p>
      <p className={`font-mono text-4xl mt-2 tracking-tight ${accent ? "text-[#CC1A1A]" : "text-white"}`}>
        {value}
      </p>
    </div>
  );
}
